import React, { createContext, useState } from 'react';

import useAuth from '../hooks/useAuth';
import useCurrentSchool from '../hooks/useCurrentSchool';
import useSchoolChildcare from '../hooks/useSchoolChildcare';
import useCurrentFranchisee from '../hooks/useCurrentFranchisee';
import usePushNotifications from '../hooks/usePushNotifications';
import UserService from '../services/User';
import Messaging from '../services/Messaging';

type AuthContextType = {
  token?: string;
  tokenExpires?: number;
  user?: any;
  appVersion?: string;
  authenticated?: boolean;
  loading?: boolean;
  handleLogin: (email: string, password: string, captcha: string) => Promise<void | { error: string }>;
  handleBiometricLogin: () => Promise<void | { error?: string; cancelled?: boolean; disabled?: boolean }>;
  enableBiometricLogin: (email: string) => Promise<boolean>;
  disableBiometricLogin: (currentToken?: string) => Promise<boolean>;
  handleLogout: () => void;
  handleCancelAccount: () => void;
  handleClearToken: () => void;
  getProfile: () => void;

  currentSchool?: any;
  changeSchool?: (school: any) => void;
  lastSchool?: string;
  setLastSchool?: (id: string) => void;
  childcareEnabled?: boolean;
  childcareResolved?: boolean;

  currentFranchisee?: any;
  changeFranchisee?: (school: any) => void;
  lastFranchisee?: string;
  setLastFranchisee?: (id: string) => void;
  franchisee?: any;
  userNotifications?: any;
  updateUserNotifications?: (userId: string) => void;
  setUserNotifications?: (notifications: any) => void;

  userTotalUnreadMessages?: number;
  setUserTotalUnredMessages?: (totalUnred: number) => void;

  updateuserTotalUnreadMessages?: () => void;

  sideBarIsMini?: boolean;
  updateSideBarIsMini?: (isMini: boolean) => void;
};

const Context = createContext({} as AuthContextType);

const AuthProvider: React.FC = ({ children }) => {
  const {
    token,
    tokenExpires,
    user,
    appVersion,
    authenticated,
    loading,
    handleLogin,
    handleBiometricLogin,
    enableBiometricLogin,
    disableBiometricLogin,
    handleLogout,
    handleCancelAccount,
    handleClearToken,
    getProfile,
  } = useAuth();
  const { currentSchool, changeSchool, lastSchool, setLastSchool } = useCurrentSchool();
  const { childcareEnabled, childcareResolved } = useSchoolChildcare(token, user, appVersion, currentSchool?.value);
  const { currentFranchisee, changeFranchisee, lastFranchisee, setLastFranchisee } = useCurrentFranchisee();
  usePushNotifications(Boolean(authenticated), token);
  const [userNotifications, setUserNotifications] = useState<[]>([]);
  const [userTotalUnreadMessages, setUserTotalUnredMessages] = useState(0);
  const [sideBarIsMini, setSideBarIsMini] = useState(false);

  const updateuserTotalUnreadMessages = async () => {
    if (!childcareEnabled || !currentSchool?.value) {
      return;
    }
    try {
      const total = await Messaging(token).getTotalUnreadMessagesForLoggedUser(currentSchool.value);
      if (total.data !== userTotalUnreadMessages) setUserTotalUnredMessages(total.data || 0);
    } catch (e) {
      console.error(e);
    }
  };
  const updateSideBarIsMini = (isMini: boolean) => {
    setSideBarIsMini(isMini);
  };

  const updateUserNotifications = async () => {
    try {
      const response = await UserService(token).notifications(currentSchool.value);
      setUserNotifications(response);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Context.Provider
      value={{
        token,
        tokenExpires,
        user,
        appVersion,
        loading,
        authenticated,
        handleLogin,
        handleBiometricLogin,
        enableBiometricLogin,
        disableBiometricLogin,
        handleLogout,
        handleCancelAccount,
        handleClearToken,
        getProfile,

        currentSchool,
        changeSchool,
        lastSchool,
        setLastSchool,
        childcareEnabled,
        childcareResolved,

        currentFranchisee,
        changeFranchisee,
        lastFranchisee,
        setLastFranchisee,

        userNotifications,
        setUserNotifications,
        updateUserNotifications,

        userTotalUnreadMessages,
        setUserTotalUnredMessages,

        updateuserTotalUnreadMessages,

        updateSideBarIsMini,
        sideBarIsMini,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export { Context, AuthProvider };
